/*
  assets/cart-drawer.js  —  Minimog cart drawer + Purist bundle support

  What changed vs. the stock file:
  - refresh() / refreshAndOpen(): re-fetch `/cart?section_id=cart-drawer` and swap
    the drawer's inner blocks. Exposed on the element AND on window.PuristCartDrawer,
    so purist-bundle-offers*.js can open the native drawer after a bundle add.
  - Listens to `purist:cart:refresh` and `cart:refresh` (debounced) so any cart change
    made by the gift guard / bundle JS re-renders the drawer.
  - Bundle card interactions: [data-purist-bundle-toggle] expands/collapses the item
    list (state remembered per session), [data-purist-remove-bundle] removes EVERY
    line that shares that `_purist_bundle_id` with one /cart/update.js call.
  - [data-purist-continue-shopping] closes the drawer.
  - Two extra re-render targets: [data-purist-cart-header], [data-purist-cart-summary].
  - Null-safe rendering (a missing block never throws).
*/

const PURIST_OPEN_BUNDLES_KEY = 'purist:cart:open-bundles';

function puristRootUrl() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function puristCartUrl() {
  return (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.cart) || puristRootUrl() + 'cart';
}

function puristReadOpenBundles() {
  try {
    const raw = window.sessionStorage.getItem(PURIST_OPEN_BUNDLES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list : []);
  } catch (error) {
    return new Set();
  }
}

function puristWriteOpenBundles(set) {
  try {
    window.sessionStorage.setItem(PURIST_OPEN_BUNDLES_KEY, JSON.stringify(Array.from(set)));
  } catch (error) {
    /* private mode / storage blocked: state just lives in memory */
  }
}

class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = puristRootUrl();

    this.refreshTimer = null;
    this.refreshing = null;
    this.removingBundle = false;
    this.openBundles = puristReadOpenBundles();

    this.setHeaderCartIconAccessibility();
    if (this.cartDrawerCloseIcon) this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));

    this.addEventListener("click", (event) => {
      if (this.handleInteraction(event)) return;

      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });

    this.boundScheduleRefresh = () => this.scheduleRefresh();
    document.addEventListener("purist:cart:refresh", this.boundScheduleRefresh);
    document.addEventListener("cart:refresh", this.boundScheduleRefresh);

    this.restoreBundleState();
  }

  disconnectedCallback() {
    document.removeEventListener("purist:cart:refresh", this.boundScheduleRefresh);
    document.removeEventListener("cart:refresh", this.boundScheduleRefresh);
  }

  /* ------------------------------------------------------------------ */
  /* Interactions inside the drawer                                      */
  /* ------------------------------------------------------------------ */

  handleInteraction(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return false;

    const toggle = target.closest("[data-purist-bundle-toggle]");
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      this.toggleBundle(toggle.closest("[data-purist-bundle-group]"));
      return true;
    }

    const removeButton = target.closest("[data-purist-remove-bundle]");
    if (removeButton && this.contains(removeButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.removeBundle(
        removeButton.getAttribute("data-purist-remove-bundle"),
        removeButton.closest("[data-purist-bundle-group]")
      );
      return true;
    }

    const continueShopping = target.closest("[data-purist-continue-shopping]");
    if (continueShopping && this.contains(continueShopping)) {
      event.preventDefault();
      this.close();
      return true;
    }

    return false;
  }

  toggleBundle(group, forceOpen) {
    if (!group) return;

    const bundleId = group.getAttribute("data-purist-bundle-id") || "";
    const items = group.querySelector("[data-purist-bundle-items]");
    const toggle = group.querySelector("[data-purist-bundle-toggle]");
    const open = typeof forceOpen === "boolean" ? forceOpen : !group.classList.contains("is-open");

    group.classList.toggle("is-open", open);
    if (items) items.hidden = !open;
    if (toggle) {
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      toggle.setAttribute("aria-label", open ? "Hide bundle items" : "Show bundle items");
    }

    if (bundleId) {
      if (open) this.openBundles.add(bundleId);
      else this.openBundles.delete(bundleId);
      puristWriteOpenBundles(this.openBundles);
    }
  }

  restoreBundleState() {
    const defaultOpen = this.getAttribute("data-bundle-expanded-default") === "true";

    this.querySelectorAll("[data-purist-bundle-group]").forEach((group) => {
      const bundleId = group.getAttribute("data-purist-bundle-id") || "";
      const shouldOpen = bundleId && this.openBundles.has(bundleId) ? true : defaultOpen;
      this.toggleBundle(group, shouldOpen);
    });
  }

  async removeBundle(bundleId, group) {
    if (!bundleId || this.removingBundle) return;
    this.removingBundle = true;

    const itemsElement = this.querySelector("m-cart-drawer-items");
    const loading = itemsElement && itemsElement.loading;

    if (group) group.classList.add("is-removing");
    if (loading && typeof loading.start === "function") loading.start();

    try {
      const cartResponse = await fetch(this.rootUrl + "cart.js", { headers: { Accept: "application/json" } });
      const cart = await cartResponse.json();
      const updates = {};

      (cart.items || []).forEach((item) => {
        const properties = item && item.properties ? item.properties : {};
        if (properties._purist_bundle_id === bundleId && item.key) updates[item.key] = 0;
      });

      if (!Object.keys(updates).length) {
        await this.refresh();
        return;
      }

      const response = await fetch(this.rootUrl + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          updates,
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();
      if (!response.ok) {
        throw new Error(state.description || state.message || "Unable to remove bundle.");
      }

      const sectionHtml = state.sections && state.sections["cart-drawer"];
      if (sectionHtml) {
        this.renderFromSectionHtml(sectionHtml);
      } else {
        await this.refresh();
      }

      this.classList.toggle("m-cart--empty", Number(state.item_count || 0) === 0);
      this.updateCartCount(Number(state.item_count || 0));
      this.openBundles.delete(bundleId);
      puristWriteOpenBundles(this.openBundles);

      this.emitThemeCartUpdate(state);
      document.dispatchEvent(new CustomEvent("purist:bundle-removed", { bubbles: true, detail: { bundleId, cart: state } }));

      if (window.PuristGiftGuard && typeof window.PuristGiftGuard.scheduleFastGuard === "function") {
        window.PuristGiftGuard.scheduleFastGuard();
      }
    } catch (error) {
      console.warn("[Purist cart] remove bundle failed:", error);
      if (group) group.classList.remove("is-removing");
      await this.refresh();
    } finally {
      this.removingBundle = false;
      if (loading && typeof loading.finish === "function") loading.finish();
    }
  }

  emitThemeCartUpdate(state) {
    try {
      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { ...state, source: "main-cart-items" });
      }
    } catch (error) {
      /* do nothing */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                        */
  /* ------------------------------------------------------------------ */

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (window.MinimogSettings && window.MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      if (this.cartDrawerInner) this.cartDrawerInner.style.setProperty("--translate-x", "0");
    });
    try {
      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
      }
    } catch (error) {
      /* do nothing */
    }
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    if (this.cartDrawerInner) this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  isOpen() {
    return this.classList.contains("m-cart-drawer--active");
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  /* Called by the theme's product-form.js after a normal add to cart. */
  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState && parsedState.id;

    const sectionHtml = parsedState && parsedState.sections && parsedState.sections["cart-drawer"];
    if (sectionHtml) this.renderFromSectionHtml(sectionHtml);

    setTimeout(() => {
      this.open();
    });

    if (window.PuristGiftGuard && typeof window.PuristGiftGuard.scheduleFastGuard === "function") {
      window.PuristGiftGuard.scheduleFastGuard();
    }
  }

  renderFromSectionHtml(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");

    this.getSectionsToRender().forEach((section) => {
      const incoming = parsed.querySelector(section.selector);
      const current = this.querySelector(section.selector) || document.querySelector(section.selector);
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    });

    const incomingDrawer = parsed.querySelector("m-cart-drawer");
    if (incomingDrawer) {
      this.classList.toggle("m-cart--empty", incomingDrawer.classList.contains("m-cart--empty"));
    }

    this.restoreBundleState();
    this.dispatchEvent(new CustomEvent("purist:cart-drawer:rendered", { bubbles: true }));
  }

  scheduleRefresh(delay = 120) {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refresh(), delay);
  }

  refresh() {
    if (this.refreshing) return this.refreshing;

    this.refreshing = fetch(`${puristCartUrl()}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderFromSectionHtml(responseText);
        return this.getCart();
      })
      .then((cart) => {
        if (!cart) return;
        this.classList.toggle("m-cart--empty", Number(cart.item_count || 0) === 0);
        this.updateCartCount(Number(cart.item_count || 0));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  refreshAndOpen(triggeredBy) {
    const itemsElement = this.querySelector("m-cart-drawer-items");
    const loading = itemsElement && itemsElement.loading;

    this.open(triggeredBy);
    if (loading && typeof loading.start === "function") loading.start();

    return this.refresh().finally(() => {
      if (loading && typeof loading.finish === "function") loading.finish();
    });
  }

  /* Kept for compatibility with other theme scripts that call it. */
  onCartDrawerUpdate() {
    return this.refresh();
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    if (typeof fetchJSON === "function") return fetchJSON(this.rootUrl + "cart.json");
    return fetch(this.rootUrl + "cart.js", { headers: { Accept: "application/json" } }).then((r) => r.json());
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const element = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return element ? element.innerHTML : "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-purist-cart-header]",
        block: "cart-header",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-purist-cart-summary]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("m-cart-drawer")) customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-purist-cart-header]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-purist-cart-summary]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

if (!customElements.get("m-cart-drawer-items")) customElements.define("m-cart-drawer-items", MCartDrawerItems);

/*
  Small global API used by purist-bundle-offers.js / purist-bundle-offers-subscription.js
  (and handy for any other script): window.PuristCartDrawer.refreshAndOpen()
*/
window.PuristCartDrawer = {
  element() {
    return document.querySelector("m-cart-drawer#MinimogCartDrawer") || document.querySelector("m-cart-drawer");
  },
  isAvailable() {
    const drawer = this.element();
    return Boolean(drawer && typeof drawer.refresh === "function");
  },
  open(triggeredBy) {
    const drawer = this.element();
    if (drawer && typeof drawer.open === "function") drawer.open(triggeredBy);
  },
  close() {
    const drawer = this.element();
    if (drawer && typeof drawer.close === "function") drawer.close();
  },
  refresh() {
    const drawer = this.element();
    return drawer && typeof drawer.refresh === "function" ? drawer.refresh() : Promise.resolve();
  },
  refreshAndOpen(triggeredBy) {
    const drawer = this.element();
    return drawer && typeof drawer.refreshAndOpen === "function" ? drawer.refreshAndOpen(triggeredBy) : Promise.resolve();
  },
};
